import { type NextPageWithLayout } from 'next'
import { useRouter } from 'next/router'
import queryString from 'query-string'
import React, { useEffect } from 'react'
import { DATA_TEST_ID } from 'shared-constants/build/testIds'
import { Flex } from 'components/Layout'
import { Loader, LOADER_SIZE } from 'components/Loader'
import { environment } from 'constants/environment'
import { ROUTE } from 'constants/routes'
import { useUserAuth } from 'providers/userAuth'
import { USER_TYPE } from 'providers/userAuthUtil'

interface WithAuthorizationProps {
  allowedUserType: (typeof USER_TYPE)[keyof typeof USER_TYPE][]
  redirectRoute: string
}

const withAuthorization =
  ({ allowedUserType, redirectRoute }: WithAuthorizationProps) =>
  <CP extends object>(Component: NextPageWithLayout<CP>) => {
    const WrappedComponent = (props) => {
      const { signedInUserType, isPageLoading, user, isUserSigningOut } =
        useUserAuth()
      const router = useRouter()
      useEffect(() => {
        if (
          !allowedUserType.includes(signedInUserType) &&
          !isPageLoading &&
          router.isReady
        ) {
          if (isUserSigningOut.current) {
            router.replace(redirectRoute)
          } else {
            const { url, query } = queryString.parseUrl(redirectRoute)
            query.returnUrl = `${environment.APP_URL}${router.asPath}`
            router.replace(
              queryString.stringifyUrl({
                url,
                query,
              })
            )
          }
        }
      }, [router, signedInUserType, isPageLoading, isUserSigningOut])
      if (
        user === null ||
        isPageLoading ||
        !allowedUserType.includes(signedInUserType)
      ) {
        return (
          <Flex alignItems="center" justifyContent="center" height="100vh">
            <Loader
              data-test-id={DATA_TEST_ID.PAGE_LOADER}
              size={LOADER_SIZE.XXL}
            />
          </Flex>
        )
      }
      return <Component {...props} />
    }
    WrappedComponent.getLayout = Component.getLayout
    WrappedComponent.seoProps = Component.seoProps
    return WrappedComponent
  }

export const withClientAuthorization = withAuthorization({
  allowedUserType: [USER_TYPE.CLIENT],
  redirectRoute: ROUTE.SIGN_IN(),
})

export const withExpertAuthorization = withAuthorization({
  allowedUserType: [USER_TYPE.EXPERT],
  redirectRoute: ROUTE.EXPERT_SIGN_IN(),
})

export const withClientOrExpertAuthorization = withAuthorization({
  allowedUserType: [USER_TYPE.CLIENT, USER_TYPE.EXPERT],
  redirectRoute: ROUTE.SIGN_IN(),
})

export const withAdminAuthorization = withAuthorization({
  allowedUserType: [USER_TYPE.ADMIN],
  redirectRoute: ROUTE.ADMIN_SIGN_IN(),
})
